// GenCC gene–disease validity, shown against a gene (TGCLOUD-1794, Phase 2/4).
//
// A READ-ONLY annotation layer. It is derived data, never component state:
// GenotypeModal's `useFreshEntity` re-seeds every state field on a background
// refetch and `hasUserEditedRef` guards the dirty flag, so a block that entered
// that state machine would either be clobbered or would falsely mark the modal
// unsaved. Nothing here calls markDirty(). The one piece of local state — which
// rows are open — is deliberately kept inside this component.
//
// ── SHAPED AFTER GenCC's OWN GENE PAGE, WITH ONE DELIBERATE DIVERGENCE ─────
// One collapsible row per GenCC "disease equivalent", with a right-aligned
// strip of count pills and the submitter detail behind the expansion — the
// layout of thegencc.org's gene view.
//
// The divergence is the pill. GenCC's pills are a colour and a bare number;
// the classification is named only in a 45°-rotated column header above the
// grid. We have no room for that header at 818px, and a rotated header does
// not print — so OUR pill carries the word: `Strong ×2`. That is the single
// place this surface knowingly does not look like GenCC, and it is not
// cosmetic:
//
// ── ACCESSIBILITY AND PRINT ───────────────────────────────────────────────
// The grade is ALWAYS the word. Colour is decoration on top of text that
// already says "Definitive" or "Refuted Evidence", never the carrier of the
// meaning. That is not only a WCAG point here: the pedigree and its records are
// routinely printed in monochrome, and the established finding in this codebase
// is that a monochrome print cannot distinguish clinical colours at all.
// Conflict is likewise a word plus an icon, not a hue, and it is stated on the
// COLLAPSED row so it can never hide behind an expansion.
//
// The off-scale value (Supportive) is marked by SHAPE — square corners, a
// dashed border, a divider and a dagger — because border-style and radius
// survive a greyscale photocopy and a fill colour does not.
//
// Every colour is set inline via `sx` on purpose. The monochrome test strips
// inline styles only, so a class-based or themed colour would survive the strip
// and let a colour-only regression pass falsely.

import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { styled } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CLINICAL_COLOURS } from 'src/theme/palette';

import {
  GENE_VALIDITY_STATE,
  buildGeneValidity,
  classificationLabel,
  defaultExpandedKeys,
  emptyStateCopy,
  genccGeneUrl,
  isUngradedScale,
  pmidList,
  pubmedSearchUrl
} from './geneValidity';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { useMonthDate } from 'src/i18n/dates';

// Colour is redundant with the text in every case — see the note above.
// Deliberately muted: this is reference data sitting beside the clinician's own
// record, and it must not out-shout it.
const CHIP_SX = {
  Definitive: { bgcolor: '#e8f5e9', color: '#1b5e20', borderColor: '#a5d6a7' },
  Strong: { bgcolor: '#e8f5e9', color: '#2e7d32', borderColor: '#c8e6c9' },
  Moderate: { bgcolor: '#f1f8e9', color: '#558b2f', borderColor: '#dcedc8' },
  Supportive: { bgcolor: '#eceff1', color: '#455a64', borderColor: '#cfd8dc' },
  Limited: { bgcolor: '#fff8e1', color: '#8d6e00', borderColor: '#ffe082' },
  'Disputed Evidence': {
    bgcolor: '#fff3e0',
    color: '#bf5000',
    borderColor: '#ffcc80'
  },
  'Refuted Evidence': {
    bgcolor: '#ffebee',
    color: '#b71c1c',
    borderColor: '#ef9a9a'
  },
  'No Known Disease Relationship': {
    bgcolor: '#ffebee',
    color: '#8e0000',
    borderColor: '#ef9a9a'
  }
};

const FALLBACK_SX = {
  bgcolor: '#f5f5f5',
  color: '#424242',
  borderColor: '#e0e0e0'
};

const paletteFor = (classification) => CHIP_SX[classification] || FALLBACK_SX;

// Visually hidden, but present for a screen reader. A local const rather than
// an import: @mui/utils is only a transitive dependency here. Strings: in `sx`
// a bare 1 means 100%, a full-size box where one pixel was meant.
const SR_ONLY = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap'
};

const UNGRADED_NOTE =
  "Supportive is Orphanet's only classification. It is not a point on " +
  "ClinGen's evidence scale and should not be read as one.";

const Accordion = styled(MuiAccordion)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: 0,
  boxShadow: 'none',
  '&:not(:last-of-type)': { borderBottom: 0 },
  '&::before': { display: 'none' },
  '&.Mui-expanded': { margin: 0 }
}));

const AccordionSummary = styled(MuiAccordionSummary)({
  minHeight: 40,
  padding: '0 8px',
  // Repeated under .Mui-expanded so a row does not grow as it opens.
  '&.Mui-expanded': { minHeight: 40 },
  '& .MuiAccordionSummary-content': {
    margin: '6px 0',
    // Without this the label column cannot shrink and the pill strip is pushed
    // off the right edge — the TGCLOUD-1669 lesson, as applied in
    // SidebarSection.jsx.
    minWidth: 0
  },
  '& .MuiAccordionSummary-content.Mui-expanded': { margin: '6px 0' },
  '& .MuiAccordionSummary-expandIconWrapper': { color: '#78909c' }
});

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  backgroundColor: '#fafbfc',
  borderTop: '1px solid',
  borderTopColor: theme.palette.divider,
  padding: '8px 10px 10px 30px'
}));

/**
 * One count pill: `Strong ×2`.
 *
 * The `×` is load-bearing. `Definitive 1` beside a disease genuinely named
 * "thanatophoric dysplasia type 1" reads as a tier; `Definitive ×1` cannot.
 */
const TallyPill = ({ entry }) => {
  const t = useAutoTranslation();
  const palette = paletteFor(entry.classification);
  return (
    <Box
      aria-hidden="true"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 0.75,
        py: '1px',
        // Shape carries the off-scale distinction, so it survives monochrome.
        borderRadius: entry.offScale ? '2px' : '10px',
        border: '1px solid',
        borderStyle: entry.offScale ? 'dashed' : 'solid',
        // rem, not px: WCAG 1.4.4 applies to the text this design makes
        // load-bearing.
        fontSize: '0.6875rem',
        fontWeight: 600,
        lineHeight: 1.6,
        whiteSpace: 'nowrap',
        ...palette,
        ...(entry.offScale ? { bgcolor: 'transparent' } : null)
      }}
    >
      {`${classificationLabel(entry.classification, t)} ×${entry.count}`}
      {entry.offScale ? ' †' : ''}
    </Box>
  );
};

/**
 * The strip. Off-scale pills are pushed behind a divider by tallyOf(), so
 * position never implies that Supportive outranks Limited.
 */
const TallyStrip = ({ tally }) => {
  const firstOffScale = tally.findIndex((t) => t.offScale);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'wrap',
        justifyContent: 'flex-end'
      }}
    >
      {tally.map((entry, i) => (
        <React.Fragment key={entry.classification}>
          {i === firstOffScale && i > 0 && (
            <Box
              aria-hidden="true"
              sx={{
                width: '1px',
                alignSelf: 'stretch',
                bgcolor: '#cfd8dc',
                mx: 0.25
              }}
            />
          )}
          <TallyPill entry={entry} />
        </React.Fragment>
      ))}
    </Box>
  );
};

/**
 * What a screen reader hears instead of the pills, which are aria-hidden.
 * Built from the same `tally`, and placed LAST in the summary so the disease
 * name is announced first.
 */
function verdictSentence(group, t) {
  const parts = group.tally.map((entry) => {
    const vars = {
      classification: classificationLabel(entry.classification, t),
      count: entry.count
    };
    const verdict =
      entry.count === 1
        ? t('{{classification}}, {{count}} submission', vars)
        : t('{{classification}}, {{count}} submissions', vars);
    return entry.offScale
      ? t(
          "{{verdict}} — Orphanet's own scale, not a point on ClinGen's evidence scale",
          { verdict }
        )
      : verdict;
  });
  return t('Submitter verdicts: {{parts}}.', {
    parts: parts.join('. ')
  });
}

/** 'Submitters disagree', or the truthful singular when it is one lab twice. */
const ConflictLine = ({ group }) => {
  const t = useAutoTranslation();

  if (!group.conflicting) return null;
  const text = group.submittersDisagree
    ? t('Submitters disagree')
    : t('{{submitter}} reports both {{classifications}}', {
        submitter: group.selfContradiction?.submitter,
        classifications: (group.selfContradiction?.classifications || [])
          .map((c) => classificationLabel(c, t))
          .reduce(
            (joined, c) =>
              joined
                ? t('{{first}} and {{second}}', { first: joined, second: c })
                : c,
            ''
          )
      });
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <WarningAmberIcon
        aria-hidden="true"
        sx={{ fontSize: 14, color: CLINICAL_COLOURS.classificationsDiffer }}
      />
      <Typography variant="caption" sx={{ color: '#8d5700', fontWeight: 600 }}>
        {text}
      </Typography>
    </Box>
  );
};

const LinkOut = ({ href, children }) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{ fontSize: 10.5 }}
  >
    {children}
  </Link>
);

/**
 * One submitter's verdict. Unchanged in substance from Phase 2 — the caption
 * is still a single text node of `submitter · MOI · date` — with the two
 * further links GenCC gives us. Report alone covers 47.4% of submissions;
 * report, criteria and citations together cover all of them.
 */
const SubmitterLine = ({ a }) => {
  const t = useAutoTranslation();
  const monthDate = useMonthDate();

  const bits = [
    a.submitter,
    // moiHpoCode is NULL where GenCC meant "Unknown" — it reuses HP:0000005,
    // the mode-of-inheritance subtree ROOT, for that. Show the title only when
    // there is a real mode behind it, so the row never asserts
    // "Mode of inheritance" as if it were an inheritance model.
    a.moiHpoCode ? a.moiTitle : null,
    // UTC, like the provenance line. GenCC's submitted_as_date is a CALENDAR
    // date from the submitter, not an instant: 9,989 of 30,321 values carry a
    // 'Z' and 9,567 of those sit at UTC midnight, so a local read moves the day
    // west of UTC — and for the 109 dated the 1st of a month it moves the
    // MONTH, which is the part we display. The other 20,332 values carry no
    // zone at all, and moment.utc reads their literal components, so they are
    // unchanged.
    a.submittedDate
      ? monthDate(a.submittedDate, 'MMM YYYY', { utc: true })
      : null
  ].filter(Boolean);

  const pmids = pmidList(a.pmids);
  const pubmed = pubmedSearchUrl(pmids);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        flexWrap: 'wrap'
      }}
    >
      <Chip
        size="small"
        variant="outlined"
        label={classificationLabel(a.classification, t)}
        sx={{
          height: 20,
          fontSize: 11,
          fontWeight: 600,
          border: '1px solid',
          ...paletteFor(a.classification)
        }}
      />
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {bits.join(' · ')}
      </Typography>
      {isUngradedScale(a.classification) && (
        <Tooltip title={t(UNGRADED_NOTE)}>
          <InfoOutlinedIcon sx={{ fontSize: 13, opacity: 0.5 }} />
        </Tooltip>
      )}
      {a.publicReportUrl && (
        <LinkOut href={a.publicReportUrl}>{t('report')}</LinkOut>
      )}
      {a.assertionCriteriaUrl && (
        <LinkOut href={a.assertionCriteriaUrl}>{t('criteria')}</LinkOut>
      )}
      {pubmed && (
        <LinkOut href={pubmed}>
          {pmids.length === 1
            ? t('{{count}} PMID', { count: pmids.length })
            : t('{{count}} PMIDs', { count: pmids.length })}
        </LinkOut>
      )}
    </Box>
  );
};

/**
 * The expansion. On a merged row the submitters stay grouped under the
 * catalogue entry they actually curated against, with that entry's own title
 * verbatim — so nothing about the merge hides which identifier a lab assessed.
 */
const GroupDetail = ({ group }) => (
  <Box sx={{ display: 'grid', gap: 0.75 }}>
    {group.sources.map((source) => (
      <Box key={source.code}>
        {group.merged && (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 600 }}
          >
            {[source.code, source.title].filter(Boolean).join(' · ')}
          </Typography>
        )}
        <Box sx={{ display: 'grid', gap: 0.4, mt: group.merged ? 0.3 : 0 }}>
          {source.submitters.map((a) => (
            <SubmitterLine key={a.sgcId} a={a} />
          ))}
        </Box>
      </Box>
    ))}
  </Box>
);

const DiseaseAccordion = ({ group, expanded, onToggle }) => {
  const t = useAutoTranslation();
  const headingId = `gencc-${group.key}-heading`;
  const panelId = `gencc-${group.key}-panel`;
  // A merged row shows the MONDO id that justifies the merge; an unmerged one
  // does not, because there the submitted code IS the identity of the row.
  const codes = group.merged
    ? [group.mondo, ...group.codes.filter((c) => c !== group.mondo)].filter(
        Boolean
      )
    : group.codes;

  return (
    <Accordion
      square
      disableGutters
      expanded={expanded}
      onChange={onToggle}
      // No unmountOnExit: a collapsed panel stays in the DOM so the print rule
      // can reveal it. CSS cannot show what was never rendered.
      slotProps={{ transition: { unmountOnExit: false } }}
    >
      {/* The heading wraps the summary rather than sitting inside it: the
          summary is a ButtonBase, i.e. a real <button>, which may only contain
          phrasing content. MUI's Accordion reads `id` and `aria-controls` off
          its FIRST child to wire the region, so they belong here. */}
      <Box
        component="h4"
        id={headingId}
        aria-controls={panelId}
        sx={{ m: 0, font: 'inherit' }}
      >
        <AccordionSummary
          aria-controls={panelId}
          expandIcon={<ArrowDropDownIcon />}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              columnGap: 1.5,
              alignItems: 'center',
              width: '100%',
              minWidth: 0
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {group.label}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  flexWrap: 'wrap',
                  alignItems: 'center'
                }}
              >
                {codes.map((code, i) => (
                  <React.Fragment key={code}>
                    {i > 0 && (
                      <Typography
                        aria-hidden="true"
                        variant="caption"
                        sx={{ color: 'text.disabled' }}
                      >
                        ·
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      {code}
                    </Typography>
                  </React.Fragment>
                ))}
              </Box>
              <ConflictLine group={group} />
            </Box>
            <TallyStrip tally={group.tally} />
            {/* Last, so the disease name is announced before the verdicts. */}
            <Box component="span" sx={SR_ONLY}>
              {verdictSentence(group, t)}
            </Box>
          </Box>
        </AccordionSummary>
      </Box>
      <AccordionDetails>
        <GroupDetail group={group} />
      </AccordionDetails>
    </Accordion>
  );
};

const ValidityHeader = ({ totals, gene, allOpen, onToggleAll, multiRow }) => {
  const t = useAutoTranslation();

  const geneUrl = genccGeneUrl(gene?.hgncId);
  // Each plural is its own sentence, chosen by the counts (TGCLOUD-1893).
  const submissionVars = {
    submissions: totals.submissions,
    submitters: totals.submitters
  };
  const submissionsText =
    totals.submissions === 1
      ? totals.submitters === 1
        ? t(
            '{{submissions}} submission from {{submitters}} submitter',
            submissionVars
          )
        : t(
            '{{submissions}} submission from {{submitters}} submitters',
            submissionVars
          )
      : totals.submitters === 1
      ? t(
          '{{submissions}} submissions from {{submitters}} submitter',
          submissionVars
        )
      : t(
          '{{submissions}} submissions from {{submitters}} submitters',
          submissionVars
        );
  const counts = [
    totals.diseases === 1
      ? t('{{count}} disease', { count: totals.diseases })
      : t('{{count}} diseases', { count: totals.diseases }),
    submissionsText
  ].join(' · ');

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1
        }}
      >
        <Typography component="h3" variant="body1" sx={{ fontWeight: 'bold' }}>
          {t('Gene–disease validity')}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('Curated by GenCC · read-only')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          mb: 0.75
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {counts}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
          {multiRow && (
            <Link
              component="button"
              type="button"
              onClick={onToggleAll}
              data-print-hide=""
              sx={{ fontSize: 11 }}
            >
              {allOpen ? t('Collapse all') : t('Expand all')}
            </Link>
          )}
          {geneUrl && (
            <LinkOut href={geneUrl}>
              {t('View {{symbol}} on GenCC', { symbol: gene.symbol })}
            </LinkOut>
          )}
        </Box>
      </Box>
      {totals.conflicting > 0 && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.75 }}
        >
          <WarningAmberIcon
            aria-hidden="true"
            sx={{ fontSize: 14, color: CLINICAL_COLOURS.classificationsDiffer }}
          />
          <Typography
            variant="caption"
            sx={{ color: '#8d5700', fontWeight: 600 }}
          >
            {totals.conflicting === 1
              ? t('{{count}} disease where submitters disagree', {
                  count: totals.conflicting
                })
              : t('{{count}} diseases where submitters disagree', {
                  count: totals.conflicting
                })}
          </Typography>
        </Box>
      )}
    </>
  );
};

/**
 * 'GenCC 2026-09-20 · Retrieved 20 Sep 2026'.
 *
 * BOTH HALVES ARE READ IN UTC, and they have to be. `dataset_version` is
 * written by the seeder as `new Date().toISOString().slice(0, 10)` — a UTC
 * calendar date — so reading `downloaded_at` with local getters makes the line
 * contradict itself: at UTC−10 it rendered
 * 'GenCC 2026-09-20 · Retrieved 19 Sep 2026', a dataset retrieved the day
 * before the version it retrieved. Invisible at UTC, which is where CI runs.
 *
 * VariantRow.jsx's ClinvarProvenance builds the ClinVar equivalent — it is
 * where this line was copied from — and was fixed the same way under
 * TGCLOUD-1834.
 */
const GenccProvenance = ({ version, downloadedAt }) => {
  const t = useAutoTranslation();
  const monthDate = useMonthDate();
  const parts = [
    version ? `GenCC ${version}` : null,
    downloadedAt
      ? t('Retrieved {{date}}', {
          date: monthDate(downloadedAt, 'D MMM YYYY', { utc: true })
        })
      : null
  ].filter(Boolean);
  if (!parts.length) return null;
  return (
    <Typography sx={{ mt: 0.75, fontSize: 10.5, opacity: 0.65 }}>
      {parts.join(' · ')}
    </Typography>
  );
};

const EmptyState = ({ state, reason }) => {
  const t = useAutoTranslation();
  // emptyStateCopy keeps the reviewed English; it is translated here to show.
  const copy = emptyStateCopy(state, reason);
  return (
    <Box
      sx={{
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.25,
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start'
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 16, mt: '2px', opacity: 0.55 }} />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t(copy.heading)}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t(copy.body)}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * @param {object}  response  the /gencc/by-gene payload (or null while loading)
 * @param {boolean} loading
 */
const GeneValidityBlock = ({ response, loading, geneIdentified = true }) => {
  const t = useAutoTranslation();

  const model = useMemo(
    () => buildGeneValidity(response, { geneIdentified }),
    [response, geneIdentified]
  );
  const signature = model.diseaseGroups.map((g) => g.key).join('|');
  const defaults = useMemo(
    () => new Set(defaultExpandedKeys(model.diseaseGroups)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature]
  );
  // DERIVED with a null sentinel, not initialised. `useState(() => …)` would
  // run its initialiser once — against the `response === null` that
  // useGeneValidity starts from — return [], and never re-run, so every gene
  // would arrive collapsed. Deriving also means a background refetch of the
  // SAME gene cannot snap the reader's open rows shut, while a change of gene
  // resets cleanly.
  const [touched, setTouched] = useState({ signature: null, open: null });
  const open = touched.signature === signature ? touched.open : defaults;

  const toggle = (key) => {
    const next = new Set(open);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setTouched({ signature, open: next });
  };
  const allOpen =
    model.diseaseGroups.length > 0 &&
    model.diseaseGroups.every((g) => open.has(g.key));
  const toggleAll = () =>
    setTouched({
      signature,
      open: allOpen ? new Set() : new Set(model.diseaseGroups.map((g) => g.key))
    });

  if (loading && geneIdentified) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography
          component="h3"
          variant="body1"
          sx={{ fontWeight: 'bold', mb: 1 }}
        >
          {t('Gene–disease validity')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={12} thickness={5} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('Checking GenCC…')}
          </Typography>
        </Box>
      </Box>
    );
  }

  const empty =
    model.state === GENE_VALIDITY_STATE.UNIDENTIFIED_GENE ||
    model.state === GENE_VALIDITY_STATE.UNAVAILABLE ||
    model.state === GENE_VALIDITY_STATE.NOT_CURATED;

  if (empty) {
    return (
      <Box sx={{ mb: 2 }} data-testid="gene-validity-block">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            mb: 1
          }}
        >
          <Typography
            component="h3"
            variant="body1"
            sx={{ fontWeight: 'bold' }}
          >
            {t('Gene–disease validity')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('Curated by GenCC · read-only')}
          </Typography>
        </Box>
        <EmptyState state={model.state} reason={model.reason} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: 2,
        // Print is a requirement here, not a nicety: these records go out in
        // clinical letters. Every collapsed panel is revealed, and the control
        // that only makes sense on screen is hidden.
        '@media print': {
          '& .MuiCollapse-root': {
            height: 'auto !important',
            visibility: 'visible !important',
            overflow: 'visible !important'
          },
          '& .MuiAccordionSummary-expandIconWrapper': { display: 'none' },
          '& [data-print-hide]': { display: 'none' }
        }
      }}
      data-testid="gene-validity-block"
    >
      <ValidityHeader
        totals={model.totals}
        gene={model.gene}
        allOpen={allOpen}
        onToggleAll={toggleAll}
        multiRow={model.diseaseGroups.length > 1}
      />
      {model.diseaseGroups.map((group) => (
        <DiseaseAccordion
          key={group.key}
          group={group}
          expanded={open.has(group.key)}
          onToggle={() => toggle(group.key)}
        />
      ))}
      {model.totals.hasUngraded && (
        <Typography
          sx={{
            mt: 0.75,
            fontSize: 10.5,
            color: 'text.secondary'
          }}
        >
          {`† ${t(UNGRADED_NOTE)}`}
        </Typography>
      )}
      <GenccProvenance
        version={model.datasetVersion}
        downloadedAt={model.datasetDownloadedAt}
      />
    </Box>
  );
};

export default GeneValidityBlock;
